import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  styled,
  Popover,
  TextField,
  IconButton,
  Paper,
  LinearProgress,
} from "@mui/material";
import { useRbac } from "../../../hooks/RbacContext";
import Tab from "../../../components/Tab/Tab";
import Tabs from "../../../components/Tabs/Tabs";
import TabPanel from "../../../components/TabPanel/TabPanel";
import Button from "../../../components/Button/Button";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PersonIcon from "@mui/icons-material/Person";
import FlagIcon from "@mui/icons-material/Flag";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import HistoryIcon from "@mui/icons-material/History";
import ProjectActivity from "../ProjectActivity/ProjectActivity";
import AllMilestoneByProject from "../ProjectMilestone/AllMilestoneByProject";
import AllResourceByProject from "../../ProjectResourceMapping/ResourceByProject/AllResourceByProject";
import { useProjectApi } from "../../../hooks/useProjectApi";
import {
  CommentsHistoryPopover,
  commentPopoverProps,
  closeButtonStyles,
} from "../../../components/Table/TimesheetTableStyles";
import ProjectHistoryDetails from "../ProjectDetails/ProjectHistoryDetails";
import Tooltip from "@mui/material/Tooltip";
import FolderIcon from "@mui/icons-material/Folder";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ProjectActivityMilestone from "../ProjectActivityMilestone/ProjectActivityMilestone";
import MilestoneActivitySelectionPage from "../ProjectActivityMilestone/ProjectActivityMilestone";
const ChangeLog = "/icons/ChangeLog.png";

const ButtonGroup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  "& .buttons-row": {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  "& .action-button": {
    minWidth: "120px",
    padding: "8px 24px",
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    boxShadow: "none",
    transition: "all 0.2s ease",
    height: "36px",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "&.publish-button": {
      backgroundColor: `${theme.palette.secondary.main} !important`,
      color: `${theme.palette.typography[100]} !important`,
      "&:hover": {
        backgroundColor: `${theme.palette.secondary.dark} !important`,
      },
    },

    "&.reject-button": {
      backgroundColor: `${theme.palette.error.main} !important`,
      color: `${theme.palette.common.white} !important`,
      "&:hover": {
        backgroundColor: `${theme.palette.common.white} !important`,
        color: `${theme.palette.error.main} !important`,
        border: `1px solid ${theme.palette.error.main} !important`,
      },
    },

    "&:disabled": {
      backgroundColor: `${theme.palette.grey[200]} !important`,
      opacity: 0.6,
      color: `${theme.palette.grey[500]} !important`,
    },
  },
  "& .request-date": {
    fontSize: "8px",
    color: theme.palette.text.secondary,
    marginTop: "2px",
    textAlign: "center",
    width: "100%",
    opacity: 0.75,
    letterSpacing: "0.2px",
  },
}));

const MessageButton = styled(IconButton)(({ theme }) => ({
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  padding: "0",
  backgroundColor: "#3F49A7",
  transition: "all 0.2s ease",
  position: "relative",
  border: "none",
  minWidth: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "&:hover": {
    backgroundColor: "#2C347B",
  },

  "& .message-icon": {
    fontSize: "14px",
    color: "#FFFFFF",
  },

  "& .message-indicator": {
    position: "absolute",
    top: "2px",
    right: "2px",
    width: "4px",
    height: "4px",
    backgroundColor: "#BCE910",
    borderRadius: "50%",
    boxShadow: "0 0 0 1px #3F49A7",
  },
}));

const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const CommentStyles = styled("div")(({ theme }) => ({
  ".comment-bubble": {
    "&.owner": {
      backgroundColor: "#E8F5E9",
      marginRight: "auto",
      borderLeft: "2px solid #4CAF50",
      "&::before": {
        content: '""',
        position: "absolute",
        left: "-1px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "linear-gradient(to bottom, #4CAF50, rgba(76,175,80,0.4))",
        borderRadius: "2px",
      },
      "& .owner-name": {
        color: "#4CAF50",
      },
    },
    "&.resource-manager": {
      backgroundColor: "#FFF3E0",
      marginRight: "auto",
      borderLeft: "2px solid #FF9800",
      "&::before": {
        content: '""',
        position: "absolute",
        left: "-1px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "linear-gradient(to bottom, #FF9800, rgba(255,152,0,0.4))",
        borderRadius: "2px",
      },
      "& .resource-manager-name": {
        color: "#FF9800",
      },
    },
  },
}));

const ProjectInfoCard = styled(Paper)(({ theme }) => ({
  padding: "12px 16px",
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  marginBottom: "6px",
  width: "fit-content",
  minWidth: "500px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "24px",
  "& .left-section": {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: "0 1 auto",
  },
  "& .project-title": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& .title-container": {
      display: "flex",
      alignItems: "center",
    },
    "& .folder-icon": {
      color: "#4B66D1",
      fontSize: "20px",
      opacity: 0.9,
    },
    "& .title-text": {
      fontSize: "0.7375rem",
      fontWeight: 600,
      color: "#1A1A1A",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "300px",
      letterSpacing: "0.2px",
    },
    "& .project-code": {
      fontSize: "0.7375rem",
      fontWeight: 600,
      color: "#1A1A1A",
      letterSpacing: "0.2px",
      backgroundColor: "transparent",
      padding: "0 2px",
      marginLeft: "2px",
      display: "inline",
      height: "auto",
      "&::before": {
        content: "'('",
        marginRight: "1px",
        fontWeight: 400,
      },
      "&::after": {
        content: "')'",
        marginLeft: "1px",
        fontWeight: 400,
      },
    },
  },
  "& .date-section": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginLeft: "2px",
    backgroundColor: "#F5F5F5",
    padding: "6px 12px",
    borderRadius: "6px",
    width: "fit-content",
    maxWidth: "max-content",
    flex: "0 0 auto",
    "& .calendar-icon": {
      color: "#666666",
      fontSize: "16px",
      opacity: 0.8,
      flexShrink: 0,
    },
    "& .date-text": {
      fontSize: "0.8125rem",
      color: "#333333",
      fontWeight: 400,
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      width: "fit-content",
      flex: "0 0 auto",
      "& .separator": {
        color: "#666666",
        opacity: 0.4,
        margin: "0 4px",
        flexShrink: 0,
      },
    },
  },
  "& .budget-section": {
    minWidth: "220px",
    maxWidth: "240px",
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    "& .budget-header": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "4px",
      "& .budget-title": {
        fontSize: "0.75rem",
        fontWeight: 500,
        color: "#1A1A1A",
      },
      "& .budget-percentage": {
        fontSize: "0.875rem",
        fontWeight: 600,
        color: "#1A1A1A",
      },
    },
    "& .progress-container": {
      marginBottom: "4px",
    },
    "& .progress-bar": {
      height: "6px",
      borderRadius: "3px",
      backgroundColor: "#E0E0E0",
      "& .MuiLinearProgress-bar": {
        borderRadius: "3px",
      },
      "&.over-budget .MuiLinearProgress-bar": {
        backgroundColor: "#D32F2F",
      },
      "&:not(.over-budget) .MuiLinearProgress-bar": {
        backgroundColor: "#4CAF50",
      },
    },
    "& .budget-stats": {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      "& .percentage": {
        fontSize: "0.75rem",
        fontWeight: 500,
        color: "#1A1A1A",
      },
      "& .text": {
        fontSize: "0.75rem",
        color: "#999999",
      },
    },
  },
}));

const StatusBox = styled(Box)(({ theme, status }) => ({
  padding: "8px 24px",
  borderRadius: theme.shape.borderRadius,
  width: "120px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "4px",
  ...(status === "REJECTED" && {
    backgroundColor: theme.palette.error?.main || "#D32F2F",
    "& .status-text": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#FFFFFF",
      letterSpacing: "0.2px",
      textAlign: "center",
    },
  }),
  ...(status === "PUBLISHED" && {
    backgroundColor: `${theme.palette.grey[200]} !important`,
    "& .status-text": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: theme.palette.typography[100],
      letterSpacing: "0.2px",
      textAlign: "center",
    },
  }),
}));

const RejectedDate = styled(Typography)(({ theme }) => ({
  fontSize: "12px !important",
  color: theme.palette.text.secondary,
  marginTop: "2px",
  fontWeight: 400,
  textAlign: "center",
  width: "100%",
  opacity: 0.75,
  letterSpacing: "0.2px",
}));

const ProjectDetails = () => {
  const { id } = useParams();
  const { hasProjectPermission, permissions, checkAccess } = useRbac();
  const [value, setValue] = React.useState(0);
  const {
    getProjectById,
    updateProjectStatus,
    getProjectCommentHistory,
    addProjectComment,
    getProjectActualHours,
  } = useProjectApi();
  const [projectDetails, setProjectDetails] = useState({});
  const [projectHours, setProjectHours] = useState({
    project_budget_hours: 0,
    budget_hours_percentage: 0,
    remaining_hours_percentage: 0,
  });
  const [loading, setLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [commentAnchorEl, setCommentAnchorEl] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);

  const getVisibleTabs = () => {
    const tabs = [
      // {
      //   id: "insights",
      //   label: "Insights",
      //   icon: <ManageSearchIcon />,
      //   visible: true,
      // },
      {
        id: "members",
        label: "Members",
        icon: <PersonIcon />,
        visible: hasMemberAccess || hasMemberGlobalAccess,
      },
      {
        id: "milestones",
        label: "Milestones",
        icon: <FlagIcon />,
        visible: hasMilestoneAccess || hasMilestoneGlobalAccess,
      },
      {
        id: "activities",
        label: "Activities",
        icon: <LocalActivityIcon />,
        visible: hasActivityAccess || hasActivityGlobalAccess,
      },
      {
        id: "changelog",
        label: "",
        icon: (
          <img
            src={ChangeLog}
            alt="Change Log"
            style={{ width: 20, height: 20 }}
          />
        ),
        visible: hasChangeLogAccess || hasChangeLogGlobalAccess,
        tooltip: "View change logs",
      },
    ];

    return tabs.filter((tab) => tab.visible);
  };

  const getTabPanelIndex = (tabId) => {
    return getVisibleTabs().findIndex((tab) => tab.id === tabId);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const hasMilestoneAccess = hasProjectPermission(
    parseInt(id),
    "milestones",
    "view"
  );

  const hasChangeLogAccess = hasProjectPermission(
    parseInt(id),
    "changeLogs",
    "view"
  );

  const hasMemberAccess = hasProjectPermission(
    parseInt(id),
    "resource",
    "view"
  );

  const hasMilestoneGlobalAccess = checkAccess("projectMilestones", "view");

  const hasActivityAccess = hasProjectPermission(parseInt(id), "tasks", "view");

  const hasActivityGlobalAccess = checkAccess("projectTasks", "view");

  const hasMemberGlobalAccess = checkAccess("projectResources", "view");

  const hasChangeLogGlobalAccess = checkAccess("projectChangeLogs", "view");

  const isPrimaryManager = permissions?.projectRoles?.find((projectRole) => {
    const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
      ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
          parseInt(id.trim())
        )
      : [];
    return (
      parseInt(id) === parseInt(projectRole?.project_id) &&
      (parseInt(projectRole.role?.role_id) ===
        parseInt(process.env.REACT_APP_PM_ROLE_ID) ||
        superAdminRoleIds.includes(parseInt(projectRole.role?.role_id)))
    );
  });
  console.log("isPrimaryManager", isPrimaryManager);
  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
        parseInt(id.trim())
      )
    : [];

  const isSuperAdmin = superAdminRoleIds.includes(
    parseInt(permissions?.globalRole?.role_id)
  );

  const isProjectOwner = permissions?.projectRoles?.find(
    (projectRole) =>
      parseInt(id) === parseInt(projectRole?.project_id) &&
      parseInt(projectRole.role?.role_id) ===
        parseInt(process.env.REACT_APP_PO_ROLE_ID)
  );
  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      const [projectFetched, hoursData] = await Promise.all([
        getProjectById(id),
        getProjectActualHours(id),
      ]);

      setProjectDetails({
        name: projectFetched?.data?.project_name,
        description: projectFetched?.data?.project_description,
        status: projectFetched?.data?.publish_type,
        project_status_code: projectFetched?.data?.project_status_code,
        last_published_on: projectFetched?.data?.last_published_on,
        project_code: projectFetched?.data?.project_code,
        start_date: projectFetched?.data?.proposed_start_date,
        end_date: projectFetched?.data?.proposed_end_date,
        budget_hours: projectFetched?.data?.budget_hours,
      });

      if (hoursData?.success) {
        setProjectHours(hoursData.data);
      }

      const commentHistory = await getProjectCommentHistory(id);
      if (commentHistory?.success && commentHistory?.data) {
        setComments(
          commentHistory.data
            .map((comment) => ({
              ...comment,
              type: comment.type || "RESOURCE",
              resource_name: comment.resource_name || "You",
              created_on: comment.created_on || new Date().toISOString(),
            }))
            .reverse()
        );
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  const handleConfirmAction = (type, action) => {
    console.log("action", action, type);
    const modalConfig = {
      open: true,
      title:
        type === "publish"
          ? "Project Plan Approval"
          : `${type.charAt(0).toUpperCase() + type.slice(1)} Project`,
      message:
        type === "publish"
          ? "Are you sure you want to request for approval?"
          : `Are you sure you want to ${type.toLowerCase()} this project Plan?`,
      onConfirm: async () => {
        try {
          setLoading(true);
          await action();
          await fetchProjectDetails();
        } catch (error) {
          console.error(`Error ${type.toLowerCase()}ing project:`, error);
        } finally {
          setLoading(false);
          setConfirmationModal(null);
        }
      },
      colorCode:
        type === "approve"
          ? "#2E7D32"
          : type === "reject"
          ? "#D32F2F"
          : "#1976D2",
      submitbuttom:
        type === "publish"
          ? "Request"
          : type === "approve"
          ? "Approve"
          : type === "reject"
          ? "Reject"
          : type.charAt(0).toUpperCase() + type.slice(1),
    };
    setConfirmationModal(modalConfig);
  };
  console.log("submitbuttom", confirmationModal);
  const handleCommentClick = (event) => {
    setCommentAnchorEl(event.currentTarget);
  };

  const handleCommentClose = () => {
    setCommentAnchorEl(null);
  };

  const handleCommentSend = async () => {
    if (!newComment.trim()) return;

    try {
      let commentType = "RESOURCE";
      const isProjectOwner = permissions?.projectRoles?.find(
        (projectRole) =>
          parseInt(id) === parseInt(projectRole?.project_id) &&
          parseInt(projectRole.role?.role_id) ===
            parseInt(process.env.REACT_APP_PO_ROLE_ID)
      );

      const isPM = permissions?.projectRoles?.find(
        (projectRole) =>
          parseInt(id) === parseInt(projectRole?.project_id) &&
          parseInt(projectRole.role?.role_id) ===
            parseInt(process.env.REACT_APP_PM_ROLE_ID)
      );

      const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
        ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
            parseInt(id.trim())
          )
        : [];
      const isSuperAdmin = superAdminRoleIds.includes(
        parseInt(permissions?.globalRole?.role_id)
      );

      if (isProjectOwner) {
        commentType = "OWNER";
      } else if (isSuperAdmin) {
        commentType = "RESOURCE_MANAGER";
      } else if (isPM) {
        commentType = "MANAGER";
      }

      const result = await addProjectComment(id, newComment, commentType);

      if (result.success) {
        // Update local comments state with the new comment
        setComments((prevComments) => [...prevComments, result.data]);
        setNewComment("");
      } else {
        console.error("Failed to add comment:", result.error);
      }
    } catch (error) {
      console.error("Error handling comment:", error);
    }
  };

  const handlePublish = () => {
    console.log("updateProjectStatus", "updateProjectStatus");
    handleConfirmAction("publish", async () => {
      try {
        setLoading(true);
        // Format comments as simple strings
        const newComments = comments
          .filter((comment) => !comment.id)
          .map((c) => c.comment);
        await updateProjectStatus(id, "PUBLISHED", true, "MANAGER");
        await fetchProjectDetails();
      } catch (error) {
        console.error("Error publishing project:", error);
      } finally {
        setLoading(false);
        setConfirmationModal(null);
      }
    });
  };

  const handleApprove = () => {
    handleConfirmAction("approve", async () => {
      try {
        setLoading(true);
        // Format comments as simple strings
        const newComments = comments
          .filter((comment) => !comment.id)
          .map((c) => c.comment);
        await updateProjectStatus(id, "APPROVED", true, "OWNER");
        await fetchProjectDetails();
      } catch (error) {
        console.error("Error approving project:", error);
      } finally {
        setLoading(false);
        setConfirmationModal(null);
      }
    });
  };

  const handleReject = () => {
    handleConfirmAction("reject", async () => {
      try {
        setLoading(true);
        // Format comments as simple strings
        const newComments = comments
          .filter((comment) => !comment.id)
          .map((c) => c.comment);
        await updateProjectStatus(id, "REJECTED", true, "OWNER");
        await fetchProjectDetails();
      } catch (error) {
        console.error("Error rejecting project:", error);
      } finally {
        setLoading(false);
        setConfirmationModal(null);
      }
    });
  };

  const handleKeyPress = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleCommentSend();
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return "-";
      let date;
      if (!isNaN(timestamp)) {
        const timestampNum = Number(timestamp);
        date = new Date(
          timestampNum.toString().length > 10
            ? timestampNum
            : timestampNum * 1000
        );
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) return "-";

      return date.toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const getCommentIcon = (type) => {
    switch (type?.toUpperCase()) {
      case "OWNER":
        return (
          <SupervisorAccountIcon
            className="user-icon"
            sx={{ color: "#4CAF50" }}
          />
        );
      case "MANAGER":
        return (
          <SupervisorAccountIcon
            className="user-icon"
            sx={{ color: "#5F80F6" }}
          />
        );
      case "RESOURCE MANAGER":
        return (
          <SupervisorAccountIcon
            className="user-icon"
            sx={{ color: "#FF9800" }}
          />
        );
      default:
        return <ChatIcon className="user-icon" />;
    }
  };

  const determineCommentPosition = (commentType) => {
    if (isProjectOwner) {
      return commentType === "OWNER" ? "resource" : "manager";
    } else {
      return commentType === "OWNER" ? "manager" : "resource";
    }
  };

  return (
    <div sx={{ width: "100%", px: "0px", py: "0px" }}>
      <CommentStyles>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
            width: "100%",
          }}
        >
          <Box sx={{ maxWidth: "80%" }}>
            <ProjectInfoCard>
              <div className="left-section">
                <div className="project-title">
                  <FolderIcon className="folder-icon" />
                  <div className="title-container">
                    <Typography className="title-text">
                      {projectDetails?.name?.toUpperCase()}
                    </Typography>
                    <Typography className="project-code">
                      {projectDetails?.project_code}
                    </Typography>
                  </div>
                </div>
                <div className="date-section">
                  <CalendarTodayIcon className="calendar-icon" />
                  <Typography className="date-text">
                    {projectDetails?.start_date?.split("-").reverse().join("-")}
                    <span className="separator">|</span>
                    {projectDetails?.end_date?.split("-").reverse().join("-")}
                  </Typography>
                </div>
              </div>
              <div className="budget-section">
                <div className="budget-header">
                  <Typography className="budget-title">
                    Budget Hours {projectHours?.project_budget_hours || 0}
                  </Typography>
                  <Typography className="budget-percentage">
                    {projectHours?.budget_hours_percentage}%
                  </Typography>
                </div>
                <div className="progress-container">
                  <LinearProgress
                    className={`progress-bar ${
                      projectHours?.budget_hours_percentage > 100
                        ? "over-budget"
                        : ""
                    }`}
                    variant="determinate"
                    value={Math.min(
                      projectHours?.budget_hours_percentage || 0,
                      100
                    )}
                  />
                </div>
                <div className="budget-stats">
                  <span className="percentage">
                    {projectHours?.total_actual_hours}
                  </span>
                  <span className="text">Actual Hours used</span>
                </div>
                <div className="budget-stats" style={{ marginTop: "4px" }}>
                  <span className="percentage">
                    {projectHours?.total_estimated_hours}
                  </span>
                  <span className="text">Hours estimated in Project Plan</span>
                </div>
              </div>
            </ProjectInfoCard>

            {projectDetails?.status && (
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Typography
                  fontSize="0.75rem"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    padding: "4px 8px",
                    borderRadius: "4px",
                    backgroundColor:
                      projectDetails?.status === "APPROVED" && "#E8F5E9",
                    color: projectDetails?.status === "APPROVED" && "#2E7D32",
                  }}
                >
                  {projectDetails?.status === "APPROVED" && (
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                  )}

                  {projectDetails?.status === "APPROVED" &&
                    `Project Plan Approved`}
                </Typography>
                {projectDetails?.status === "APPROVED" && (
                  <Typography
                    fontSize="0.75rem"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    {projectDetails?.status === "APPROVED" && "Approved on: "}
                    {formatDate(projectDetails?.last_published_on)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <ButtonGroup>
                <div className="buttons-row">
                  {projectDetails?.status === "REJECTED" && isProjectOwner ? (
                    <StatusBox status="REJECTED">
                      <Typography className="status-text">Rejected</Typography>
                    </StatusBox>
                  ) : projectDetails?.status === "PUBLISHED" &&
                    isPrimaryManager ? (
                    <StatusBox status="PUBLISHED">
                      <Typography className="status-text">Requested</Typography>
                    </StatusBox>
                  ) : projectDetails?.status === "PUBLISHED" &&
                    isProjectOwner ? (
                    <>
                      <Button
                        className="action-button publish-button"
                        onClick={handleApprove}
                        disabled={loading}
                      >
                        Approve
                      </Button>
                      <Button
                        className="action-button reject-button"
                        onClick={handleReject}
                        disabled={loading}
                      >
                        Reject
                      </Button>
                    </>
                  ) : isPrimaryManager &&
                    (projectDetails?.status === null ||
                      projectDetails?.status === undefined ||
                      projectDetails?.status === "REJECTED") ? (
                    <Button
                      className="action-button publish-button"
                      onClick={handlePublish}
                      disabled={loading}
                    >
                      {projectDetails?.status === "REJECTED"
                        ? "Request for Reapproval"
                        : "Request for Approval"}
                    </Button>
                  ) : null}
                </div>
              </ButtonGroup>

              {projectDetails?.status !== "APPROVED" && (
                <Tooltip title="Comments" arrow placement="bottom">
                  <MessageButton onClick={handleCommentClick}>
                    {comments.length > 0 ? (
                      <>
                        <ChatIcon className="message-icon" />
                        <span className="message-indicator" />
                      </>
                    ) : (
                      <ChatBubbleOutlineIcon className="message-icon" />
                    )}
                  </MessageButton>
                </Tooltip>
              )}
            </Box>

            {(projectDetails?.status === "PUBLISHED" ||
              projectDetails?.status === "REJECTED") && (
              <Typography
                className="request-date"
                sx={{
                  fontSize: "12px !important",
                  opacity: 0.75,
                  letterSpacing: "0.2px",
                  fontWeight: 400,
                }}
              >
                {projectDetails?.status === "REJECTED" && isProjectOwner
                  ? `Project Plan Rejected: ${formatDate(
                      projectDetails?.last_published_on
                    )}`
                  : projectDetails?.status === "REJECTED" && isPrimaryManager
                  ? `Request Rejected on: ${formatDate(
                      projectDetails?.last_published_on
                    )}`
                  : projectDetails?.status === "PUBLISHED" && isPrimaryManager
                  ? `Requested for Approval: ${formatDate(
                      projectDetails?.last_published_on
                    )}`
                  : `Requested for Approval: ${formatDate(
                      projectDetails?.last_published_on
                    )}`}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            borderBottom: 2,
            borderTop: 2,
            borderColor: "divider",
            marginTop: 1,
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            {getVisibleTabs().map((tab, index) =>
              tab.tooltip ? (
                <Tooltip key={tab.id} title={tab.tooltip} arrow>
                  <Tab
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                    {...a11yProps(index)}
                    sx={tab.id === "changelog" ? { marginLeft: "auto" } : {}}
                  />
                </Tooltip>
              ) : (
                <Tab
                  key={tab.id}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  {...a11yProps(index)}
                />
              )
            )}
          </Tabs>
        </Box>
        {/* <TabPanel value={value} index={getTabPanelIndex("insights")}>
          Insights
        </TabPanel> */}
        {(hasMemberAccess || hasMemberGlobalAccess) && (
          <TabPanel value={value} index={getTabPanelIndex("members")}>
            <AllResourceByProject
              projectName={projectDetails?.name?.toUpperCase()}
            />
          </TabPanel>
        )}
        {(hasMilestoneAccess || hasMilestoneGlobalAccess) && (
          <TabPanel value={value} index={getTabPanelIndex("milestones")}>
            <ProjectActivity projectId={id} />
          </TabPanel>
        )}
        {(hasActivityAccess || hasActivityGlobalAccess) && (
          <TabPanel value={value} index={getTabPanelIndex("activities")}>
            <MilestoneActivitySelectionPage projectId={id} />
          </TabPanel>
        )}
        {(hasChangeLogAccess || hasChangeLogGlobalAccess) && (
          <TabPanel value={value} index={getTabPanelIndex("changelog")}>
            <ProjectHistoryDetails projectId={id} />
          </TabPanel>
        )}

        {confirmationModal && (
          <ConfirmationModal
            open={confirmationModal.open}
            title={confirmationModal.title}
            message={confirmationModal.message}
            onClose={() => setConfirmationModal(null)}
            onConfirm={confirmationModal.onConfirm}
            buttonText={confirmationModal.submitbuttom}
            colorCode={confirmationModal.colorCode}
          />
        )}

        {projectDetails?.status !== "APPROVED" && (
          <Popover
            open={Boolean(commentAnchorEl)}
            anchorEl={commentAnchorEl}
            onClose={handleCommentClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              ...commentPopoverProps,
              sx: {
                ...commentPopoverProps.sx,
                backgroundColor: "transparent",
              },
            }}
          >
            <CommentsHistoryPopover>
              <div className="comments-header">
                <Typography variant="h6">
                  <ChatIcon className="icon" />
                  Comments
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleCommentClose}
                  sx={closeButtonStyles}
                >
                  <CloseIcon />
                </IconButton>
              </div>
              <div className="comment-list">
                {comments && comments.length > 0 ? (
                  comments.map((comment, index) => (
                    <div
                      key={index}
                      className={`comment-bubble ${determineCommentPosition(
                        comment.type
                      )}`}
                    >
                      <Typography className="comment-text">
                        {comment.comment}
                      </Typography>
                      <div className="comment-meta">
                        <span className="user-name">
                          {getCommentIcon(comment.type)}
                          {comment.resource_name}
                        </span>
                        <span className="comment-date">
                          <AccessTimeIcon className="time-icon" />
                          {formatDate(comment.created_on)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">
                    <ChatBubbleOutlineIcon className="icon" />
                    <Typography className="text">
                      No comments available for this project
                    </Typography>
                  </div>
                )}
              </div>
              <div className="comment-input-container">
                <TextField
                  className="comment-input"
                  multiline
                  maxRows={4}
                  placeholder="Type a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleCommentSend();
                    }
                  }}
                  variant="outlined"
                  fullWidth
                />
                <IconButton
                  className="send-button"
                  onClick={handleCommentSend}
                  disabled={!newComment.trim()}
                >
                  <SendIcon
                    sx={{ fontSize: "16px", transform: "rotate(-45deg)" }}
                  />
                </IconButton>
              </div>
            </CommentsHistoryPopover>
          </Popover>
        )}
      </CommentStyles>
    </div>
  );
};

export default ProjectDetails;
